import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import { url, MARKETING_SITE_URL } from "../../../Config/Config";
import {
  CmsPageDetailAction,
  CmsPageUpdateApi,
  CmsPagePublishApi,
  CmsPageArchiveApi,
} from "../../../Redux/Actions/CmsAction";
import type {
  BodyJson,
  ContentPageDetail,
  PageStatus,
  SchemaJsonLd,
  SeoJson,
} from "../../../types/cms";
import { validateBodyForSave, validatePageTitle, validateSeo } from "../utils/cmsValidation";
import { stripCmsComputedUrls } from "../../../utils/cmsSanitize";
import CmsApiErrorAlert from "../shared/CmsApiErrorAlert";
import CmsConfirmDialog from "../shared/CmsConfirmDialog";
import CmsCodeMirrorJsonEditor from "../shared/CmsCodeMirrorJsonEditor";
import CmsImageUploadPanel from "../shared/CmsImageUploadPanel";
import {
  CMS_PLACEHOLDER_BODY_JSON,
  CMS_PLACEHOLDER_SCHEMA_JSONLD,
  CMS_PLACEHOLDER_SEO_JSON,
} from "../utils/cmsJsonPlaceholders";

type WorkspaceTab = "json" | "preview" | "media";

const WORKSPACE_TABS: WorkspaceTab[] = ["json", "preview", "media"];

function parseWorkspaceTab(param: string | null): WorkspaceTab {
  if (param && WORKSPACE_TABS.includes(param as WorkspaceTab)) {
    return param as WorkspaceTab;
  }
  return "json";
}

function snapshotStrings(
  title: string,
  bodyText: string,
  seoText: string,
  schemaText: string
) {
  return JSON.stringify({ title, bodyText, seoText, schemaText });
}

function tryParseJson(
  label: string,
  text: string,
  required: boolean
): { ok: true; value: unknown } | { ok: false; error: string } {
  const t = text.trim();
  if (!t) {
    if (required) {
      return { ok: false, error: `${label} is required.` };
    }
    return { ok: true, value: null };
  }
  try {
    return { ok: true, value: JSON.parse(t) };
  } catch {
    return { ok: false, error: `${label}: invalid JSON syntax.` };
  }
}

/** Stable, indented JSON for preview (2 spaces per level). */
function prettyPrintJson(value: unknown): string {
  try {
    const normalized = JSON.parse(JSON.stringify(value));
    return JSON.stringify(normalized, null, 2);
  } catch {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return "{}";
    }
  }
}

function useJsonEditorHeight() {
  return useMemo(() => {
    if (typeof window === "undefined") return 300;
    return Math.max(220, Math.min(400, Math.round(window.innerHeight * 0.24)));
  }, []);
}

function TabPanel(props: {
  value: WorkspaceTab;
  id: WorkspaceTab;
  children: React.ReactNode;
}) {
  const { value, id, children } = props;
  if (value !== id) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
}

const CmsPageEditorPage: React.FC = () => {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugParam ? decodeURIComponent(slugParam) : "";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const detailState = useSelector((state: any) => state.CmsPageDetailData);
  const editorHeight = useJsonEditorHeight();

  const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTab>(() =>
    parseWorkspaceTab(searchParams.get("tab"))
  );

  useEffect(() => {
    setWorkspaceTab(parseWorkspaceTab(searchParams.get("tab")));
  }, [searchParams]);

  const handleWorkspaceTabChange = (_: React.SyntheticEvent, next: WorkspaceTab) => {
    setWorkspaceTab(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next === "json") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", next);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const [title, setTitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [seoText, setSeoText] = useState("");
  const [schemaText, setSchemaText] = useState("");

  const [pageStatus, setPageStatus] = useState<PageStatus>("draft");
  const [markDel, setMarkDel] = useState(false);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const baseline = useRef<string>("");

  const parsedLive = useMemo(() => {
    const body = tryParseJson("body_json", bodyText, true);
    const seo = tryParseJson("seo_json", seoText, true);
    const schema = tryParseJson("schema_jsonld", schemaText, false);
    return { body, seo, schema };
  }, [bodyText, seoText, schemaText]);

  const dirty =
    slug &&
    baseline.current &&
    snapshotStrings(title, bodyText, seoText, schemaText) !== baseline.current;

  const applyDetail = useCallback((d: ContentPageDetail) => {
    setTitle(d.title);
    setBodyText(
      JSON.stringify(
        d.body_json && typeof d.body_json === "object"
          ? d.body_json
          : { schemaVersion: 1, sections: [] },
        null,
        2
      )
    );
    setSeoText(
      JSON.stringify(
        d.seo_json && typeof d.seo_json === "object"
          ? d.seo_json
          : { title: d.title },
        null,
        2
      )
    );
    setSchemaText(
      d.schema_jsonld != null && typeof d.schema_jsonld === "object"
        ? JSON.stringify(d.schema_jsonld, null, 2)
        : ""
    );
    setPageStatus(d.status);
    setMarkDel(!!d.mark_del);
    setPublishedAt(d.published_at ?? null);

    const bStr = JSON.stringify(
      d.body_json && typeof d.body_json === "object"
        ? d.body_json
        : { schemaVersion: 1, sections: [] },
      null,
      2
    );
    const sStr = JSON.stringify(
      d.seo_json && typeof d.seo_json === "object"
        ? d.seo_json
        : { title: d.title },
      null,
      2
    );
    const schStr =
      d.schema_jsonld != null && typeof d.schema_jsonld === "object"
        ? JSON.stringify(d.schema_jsonld, null, 2)
        : "";
    baseline.current = snapshotStrings(d.title, bStr, sStr, schStr);
  }, []);

  useEffect(() => {
    if (!slug) return;
    dispatch(CmsPageDetailAction(slug));
  }, [dispatch, slug]);

  useEffect(() => {
    if (
      detailState?.loading === false &&
      detailState?.status === true &&
      detailState?.data
    ) {
      applyDetail(detailState.data as ContentPageDetail);
    }
  }, [detailState, applyDetail]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const loadError =
    detailState?.loading === false &&
    detailState?.status === false &&
    detailState?.data;

  const loadErrorMessage =
    typeof loadError === "object" && loadError !== null
      ? (loadError as { message?: string }).message
      : "Failed to load page.";

  const previewContent = useMemo(() => {
    if (!parsedLive.body.ok) {
      return { type: "error" as const, errs: [parsedLive.body.error] };
    }
    if (!parsedLive.seo.ok) {
      return { type: "error" as const, errs: [parsedLive.seo.error] };
    }
    if (!parsedLive.schema.ok) {
      return { type: "error" as const, errs: [parsedLive.schema.error] };
    }

    const body = parsedLive.body.value;
    const seo = parsedLive.seo.value;
    let schema: SchemaJsonLd | null = null;
    if (parsedLive.schema.value === null || parsedLive.schema.value === undefined) {
      schema = null;
    } else if (
      typeof parsedLive.schema.value === "object" &&
      parsedLive.schema.value !== null
    ) {
      schema = parsedLive.schema.value as SchemaJsonLd;
    } else {
      return {
        type: "error" as const,
        errs: ["schema_jsonld must be a JSON object or empty."],
      };
    }
    if (typeof body !== "object" || body === null || Array.isArray(body)) {
      return { type: "error" as const, errs: ["body_json must be a JSON object."] };
    }
    if (typeof seo !== "object" || seo === null || Array.isArray(seo)) {
      return { type: "error" as const, errs: ["seo_json must be a JSON object."] };
    }
    return {
      type: "ok" as const,
      payload: {
        title: title.trim(),
        body_json: body as BodyJson,
        seo_json: seo as SeoJson,
        schema_jsonld: schema,
      },
    };
  }, [parsedLive, title]);

  const bodyFieldError = parsedLive.body.ok ? null : parsedLive.body.error;
  const seoFieldError = parsedLive.seo.ok ? null : parsedLive.seo.error;
  const schemaFieldError = parsedLive.schema.ok ? null : parsedLive.schema.error;

  const handleSave = async (): Promise<boolean> => {
    setClientError(null);
    if (markDel) {
      setClientError("This page is archived and cannot be updated.");
      return false;
    }
    const te = validatePageTitle(title);
    if (te) {
      setClientError(te);
      return false;
    }

    if (!parsedLive.body.ok || !parsedLive.seo.ok || !parsedLive.schema.ok) {
      setClientError("Fix JSON syntax errors before saving.");
      setWorkspaceTab("json");
      return false;
    }

    const bodyJson = parsedLive.body.value as BodyJson;
    const seoJson = parsedLive.seo.value as SeoJson;
    let schemaJsonld: SchemaJsonLd | null = null;
    if (parsedLive.schema.value != null) {
      schemaJsonld = parsedLive.schema.value as SchemaJsonLd;
    }

    if (typeof bodyJson !== "object" || bodyJson === null || Array.isArray(bodyJson)) {
      setClientError("body_json must be a JSON object.");
      setWorkspaceTab("json");
      return false;
    }
    if (typeof seoJson !== "object" || seoJson === null || Array.isArray(seoJson)) {
      setClientError("seo_json must be a JSON object.");
      setWorkspaceTab("json");
      return false;
    }

    const se = validateSeo(seoJson);
    if (se) {
      setClientError(se);
      setWorkspaceTab("json");
      return false;
    }
    const be = validateBodyForSave(bodyJson);
    if (be) {
      setClientError(be);
      setWorkspaceTab("json");
      return false;
    }

    const payload = stripCmsComputedUrls({
      title: title.trim(),
      body_json: bodyJson,
      seo_json: seoJson,
      schema_jsonld:
        schemaJsonld &&
        Array.isArray(schemaJsonld.documents) &&
        schemaJsonld.documents.length > 0
          ? schemaJsonld
          : null,
    });

    setSaving(true);
    const res = await CmsPageUpdateApi(slug, payload, true);
    setSaving(false);
    if (res && res.status === true) {
      baseline.current = snapshotStrings(title, bodyText, seoText, schemaText);
      dispatch(CmsPageDetailAction(slug));
      return true;
    }
    if (res && res.status === false) {
      setClientError(res.message || res.errors?.[0] || "Save failed.");
    }
    return false;
  };

  const handlePublish = async () => {
    if (markDel) return;
    if (dirty) {
      const ok = await handleSave();
      if (!ok) return;
    }
    const res = await CmsPagePublishApi(slug, true);
    if (res && res.status === true) {
      setPublishOpen(false);
      dispatch(CmsPageDetailAction(slug));
    }
  };

  const handleArchive = async () => {
    const res = await CmsPageArchiveApi(slug, true);
    if (res && res.status === true) {
      setArchiveOpen(false);
      navigate(`${url}/cms/pages`);
    }
  };

  const readonly = markDel;
  const publicBase = MARKETING_SITE_URL;

  if (!slug) {
    return (
      <Box sx={{ m: 2 }}>
        <Typography>Missing page slug.</Typography>
      </Box>
    );
  }

  return (
    <>
      {(saving || (detailState?.loading && !detailState?.data)) && <Spinner />}
      <div className="page-wrapper card">
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Edit page"
            breadcrumbs={[
              { label: "Content pages", path: `${url}/cms/pages`, isActive: false },
              { label: title || slug, path: "#", isActive: true },
            ]}
          />
        </div>

        <Box sx={{ m: 0, p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "grey.50", minHeight: "70vh" }}>
          {detailState?.loading !== false && !detailState?.data ? (
            <Typography sx={{ p: 2 }}>Loading…</Typography>
          ) : null}

          {detailState?.status === false ? (
            <Stack spacing={2} sx={{ p: 2 }}>
              <CmsApiErrorAlert message={loadErrorMessage} />
              <Button variant="outlined" onClick={() => navigate(`${url}/cms/pages`)}>
                Back to list
              </Button>
            </Stack>
          ) : null}

          {detailState?.status === true && detailState?.data ? (
            <>
              {readonly ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  This page is archived. Updates and publishing are disabled on the server.
                </Alert>
              ) : null}

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  bgcolor: "background.paper",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    background:
                      "linear-gradient(180deg, rgba(5,85,200,0.06) 0%, rgba(255,255,255,0) 100%)",
                  }}
                >
                  <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.3 }}>
                      {title || "Untitled"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Slug <strong>{slug}</strong>
                      {publishedAt ? ` · Published ${publishedAt}` : null}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
                      <Chip size="small" label={pageStatus} color="primary" variant="outlined" />
                      {dirty ? (
                        <Chip size="small" label="Unsaved changes" color="warning" />
                      ) : (
                        <Chip size="small" label="Saved" variant="outlined" />
                      )}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                    <Button
                      variant="contained"
                      disableElevation
                      disabled={readonly || !dirty || saving}
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      disabled={readonly || pageStatus === "published"}
                      onClick={() => setPublishOpen(true)}
                    >
                      Publish
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={readonly}
                      onClick={() => setArchiveOpen(true)}
                    >
                      Archive
                    </Button>
                    {pageStatus === "published" && !markDel && publicBase ? (
                      <Button
                        href={`${publicBase}/${slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View public
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>

                <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5 }}>
                  <CmsApiErrorAlert message={clientError ?? undefined} />
                </Box>

                <TextField
                  fullWidth
                  sx={{ px: { xs: 1.5, sm: 2 }, pb: 1, maxWidth: 720 }}
                  label="Page title"
                  value={title}
                  disabled={readonly}
                  inputProps={{ maxLength: 255 }}
                  onChange={(e) => setTitle(e.target.value)}
                  size="small"
                />

                <Box
                  sx={{
                    px: { xs: 1, sm: 2 },
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "rgba(15,23,42,0.02)",
                  }}
                >
                  <Tabs
                    value={workspaceTab}
                    onChange={handleWorkspaceTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                      minHeight: 52,
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        minHeight: 52,
                        fontSize: "0.95rem",
                        borderRadius: "12px 12px 0 0",
                        mx: 0.25,
                      },
                      "& .Mui-selected": { color: "primary.main" },
                      "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
                    }}
                  >
                    <Tab
                      icon={<CodeOutlinedIcon fontSize="small" />}
                      iconPosition="start"
                      label="JSON"
                      value="json"
                    />
                    <Tab
                      icon={<VisibilityOutlinedIcon fontSize="small" />}
                      iconPosition="start"
                      label="Preview"
                      value="preview"
                    />
                    <Tab
                      icon={<PermMediaOutlinedIcon fontSize="small" />}
                      iconPosition="start"
                      label="Media"
                      value="media"
                    />
                  </Tabs>
                </Box>

                <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 3 }}>
                  <TabPanel value={workspaceTab} id="json">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
                      CodeMirror 6 (open source) with JSON highlighting, line numbers, folding, and
                      format. Use <strong>Media</strong> to upload images, then paste <code>fileKey</code>{" "}
                      into your JSON.
                    </Typography>
                    <Stack spacing={2.5}>
                      <CmsCodeMirrorJsonEditor
                        label="body_json"
                        required
                        value={bodyText}
                        onChange={setBodyText}
                        height={editorHeight}
                        readOnly={readonly}
                        error={bodyFieldError}
                        helperText="Required. Public page layout: schemaVersion, sections[]. Use image + alt for images (not publicUrl)."
                        onInsertSample={() => setBodyText(CMS_PLACEHOLDER_BODY_JSON.trim())}
                      />
                      <CmsCodeMirrorJsonEditor
                        label="seo_json"
                        required
                        value={seoText}
                        onChange={setSeoText}
                        height={editorHeight}
                        readOnly={readonly}
                        error={seoFieldError}
                        helperText="Required. title, description, keywords, alternates, openGraph, robots."
                        onInsertSample={() => setSeoText(CMS_PLACEHOLDER_SEO_JSON.trim())}
                      />
                      <CmsCodeMirrorJsonEditor
                        label="schema_jsonld"
                        value={schemaText}
                        onChange={setSchemaText}
                        height={Math.max(200, editorHeight - 40)}
                        readOnly={readonly}
                        error={schemaFieldError}
                        helperText="Optional — leave empty to save as null. documents[] for JSON-LD."
                        onInsertSample={() => setSchemaText(CMS_PLACEHOLDER_SCHEMA_JSONLD.trim())}
                      />
                    </Stack>
                  </TabPanel>

                  <TabPanel value={workspaceTab} id="preview">
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: "grey.50",
                        borderStyle: "dashed",
                        textAlign: "left",
                        direction: "ltr",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        gutterBottom
                        sx={{ textAlign: "left" }}
                      >
                        Merged preview
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, textAlign: "left" }}
                      >
                        Parsed title + body_json + seo_json + schema_jsonld. Switch to{" "}
                        <strong>JSON</strong> to edit.
                      </Typography>
                      {previewContent.type === "error" ? (
                        <Stack spacing={1} alignItems="stretch" sx={{ textAlign: "left" }}>
                          {previewContent.errs.map((err, i) => (
                            <Alert
                              key={i}
                              severity="warning"
                              sx={{ borderRadius: 2, textAlign: "left", "& .MuiAlert-message": { width: "100%" } }}
                            >
                              {err}
                            </Alert>
                          ))}
                        </Stack>
                      ) : (
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 2,
                            bgcolor: "background.paper",
                            borderRadius: 2,
                            fontSize: 12,
                            lineHeight: 1.55,
                            overflow: "auto",
                            maxHeight: "min(70vh, 720px)",
                            border: "1px solid",
                            borderColor: "divider",
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
                            textAlign: "left",
                            direction: "ltr",
                            whiteSpace: "pre",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            tabSize: 2,
                            MozTabSize: 2,
                          }}
                        >
                          {prettyPrintJson(previewContent.payload)}
                        </Box>
                      )}
                    </Paper>
                  </TabPanel>

                  <TabPanel value={workspaceTab} id="media">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
                      Upload assets for this page. After upload, copy values into <strong>body_json</strong> or{" "}
                      <strong>seo_json</strong> on the JSON tab.
                    </Typography>
                    <CmsImageUploadPanel pageSlug={slug} disabled={readonly} />
                  </TabPanel>
                </Box>
              </Paper>
            </>
          ) : null}
        </Box>
      </div>

      <CmsConfirmDialog
        open={publishOpen}
        title="Publish this page?"
        description="Visitors will be able to read this page from the public API once it is published."
        confirmLabel="Publish"
        onClose={() => setPublishOpen(false)}
        onConfirm={handlePublish}
      />
      <CmsConfirmDialog
        open={archiveOpen}
        title="Archive this page?"
        description="The page will return 404 publicly and can no longer be edited."
        confirmLabel="Archive"
        danger
        onClose={() => setArchiveOpen(false)}
        onConfirm={handleArchive}
      />
    </>
  );
};

export default CmsPageEditorPage;
